// Rainbucks: every input rule in one place.
//
// Imported by the Worker (src/worker.js), which is the check that protects the
// data, and by the browser bundle (src/client/index.js -> build/rainbucks.js),
// which uses the same rules to show errors next to the field before a request
// is sent. Change a rule here and both sides follow.
import * as z from 'zod';

export const MIN_CASHOUT_CENTS = 5000; // $50.00
export const CASHOUT_METHODS = ['paypal', 'venmo', 'amazon', 'visa', 'bank', 'cashapp'];
export const BONUS_TYPES = ['signup', 'account', 'case', 'welcomeBack'];

// ---- fields ----

export const email = z
  .string({ error: 'Enter your email address.' })
  .trim()
  .toLowerCase()
  .min(1, { error: 'Enter your email address.' })
  .max(254, { error: 'That email address is too long.' })
  .pipe(z.email({ error: 'Enter a valid email address, like you@example.com.' }));

export const newPassword = z
  .string({ error: 'Choose a password.' })
  .min(8, { error: 'Use at least 8 characters.' })
  .max(128, { error: 'Use 128 characters or fewer.' });

// Logging in only checks that something was typed; the server decides if it's right.
export const currentPassword = z.string({ error: 'Enter your password.' }).min(1, { error: 'Enter your password.' });

// Letters from any alphabet, plus spaces, apostrophes, hyphens and periods.
export const legalName = z
  .string({ error: 'Enter your legal name.' })
  .trim()
  .min(1, { error: 'Enter your legal name.' })
  .max(100, { error: 'Use 100 characters or fewer.' })
  .regex(/^[\p{L}][\p{L}\p{M}' .-]*$/u, { error: 'Use letters, spaces, hyphens and apostrophes only.' })
  .refine((v) => v.replace(/[^\p{L}]/gu, '').length >= 2, { error: 'Enter your full legal name.' });

// Accepts common ways of typing a number and normalises it to E.164
// (+15551234567). A number without a country code is treated as US/Canada.
const PHONE_CHARS = /^\+?[\d\s().-]+$/;
export function normalizePhone(raw) {
  const trimmed = String(raw).trim();
  const digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('+')) return '+' + digits;
  if (digits.length === 10) return '+1' + digits;
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
  return '+' + digits;
}
export const phone = z
  .string({ error: 'Enter your phone number.' })
  .trim()
  .min(1, { error: 'Enter your phone number.' })
  .regex(PHONE_CHARS, { error: 'Use digits only, with an optional + for the country code.' })
  .transform(normalizePhone)
  .pipe(z.string().regex(/^\+[1-9]\d{7,14}$/, { error: 'Enter a valid phone number, like (555) 123-4567 or +44 20 7946 0958.' }));

// ---- forms / request bodies ----

// clickId is the ClickFlare click id (?source=... on the landing page), used to
// fire the S2S conversion postback after signup. Optional: most signups won't
// have one (direct/organic traffic).
// eventId ties the browser Meta Pixel CompleteRegistration call to the Worker's
// server-side Conversions API call for the same signup, so Meta dedupes them
// into a single conversion instead of counting both.
export const signupSchema = z.object({
  email,
  password: newPassword,
  clickId: z.string().trim().max(255).optional(),
  eventId: z.string().trim().max(255).optional(),
  marketingOptIn: z.boolean().optional(),
});
// remember controls how long the session cookie survives (see sessionCookie in
// the Worker): unchecked means a session cookie that's gone once the browser
// closes, checked (the default) means it sticks around for 30 days.
export const loginSchema = z.object({ email, password: currentPassword, remember: z.boolean().optional() });
export const accountSchema = z.object({ legalName, phone });

// The survey's email step is optional: empty means "skip".
export const surveyEmailSchema = z.object({
  email: z.union([z.literal(''), email]),
});

// Welcome-survey answers, saved one field at a time as the user answers each
// step (see SURVEY_STEPS in index.html). Every field is optional and nullable
// since "Prefer not to say" / skipping a step is always allowed.
export const AGE_BRACKETS = ['Under 18', '18-20', '21-29', '30-39', '40-49', '50+', 'Prefer not to say'];
export const PLAY_FREQS = ['Daily', 'Couple times a week', 'Less than once a week', 'Prefer not to say'];
export const surveyAnswerSchema = z.object({
  key: z.enum(['age', 'cashoutPref', 'playFreq']),
  value: z.string().trim().min(1).max(64),
});

// Google Sign-In: the browser hands us the signed ID token straight from
// Google Identity Services; the Worker verifies it and pulls sub/email out.
export const googleAuthSchema = z.object({
  credential: z.string({ error: 'Missing Google credential.' }).min(1),
  clickId: z.string().trim().max(255).optional(),
});

// Fired when a signed-in user clicks "Start" on a Glitchy offer - analytics
// only (funnel: signup -> click -> conversion), not a payout signal.
export const trackClickSchema = z.object({
  offerId: z.string().trim().max(255).optional(),
  // Human-readable offer name, as resolved client-side from Glitchy's offers
  // API - the Worker has no other way to learn it. Best-effort, for admin
  // dashboard display only.
  offerName: z.string().trim().max(255).optional(),
  // Same id the browser passes to fbq('trackCustom', 'StartedOffer', {},
  // {eventID}), so the Worker can mirror it server-side (Meta CAPI) and have
  // Meta dedupe the pair into one conversion instead of counting both.
  eventId: z.string().trim().max(255).optional(),
});

export const cashoutSchema = z.object({
  amount: z.coerce
    .number({ error: 'Amount must be a number.' })
    .int({ error: 'Amount must be whole cents.' })
    .min(MIN_CASHOUT_CENTS, { error: 'Amount is below the minimum cashout.' }),
  method: z.enum(CASHOUT_METHODS, { error: 'Choose a cashout method.' }),
  destination: z.string({ error: 'Destination is required.' }).trim().min(1, { error: 'Destination is required.' }).max(254),
});

/// Only the account-completion step remains: saves legal name + phone (still
// required at cashout time) with no bonus attached. Kept as a discriminated
// union of one so handleBonus's `body.type !== 'account'` check still has a
// typed shape to validate against.
export const bonusSchema = z.discriminatedUnion(
  'type',
  [z.object({ type: z.literal('account'), legalName, phone })],
  { error: 'Unknown bonus type.' },
);

// ---- helper ----

// Runs a schema and returns something a form can render directly:
//   { ok: true, data }                       data is parsed + normalised
//   { ok: false, fieldErrors, formError }    fieldErrors: { email: 'Enter a valid...' }
export function validate(schema, input) {
  const result = schema.safeParse(input);
  if (result.success) return { ok: true, data: result.data };
  const flat = z.flattenError(result.error);
  const fieldErrors = {};
  for (const [key, messages] of Object.entries(flat.fieldErrors)) {
    if (messages && messages.length) fieldErrors[key] = messages[0];
  }
  const formError = flat.formErrors[0] || Object.values(fieldErrors)[0] || 'Check the highlighted fields.';
  return { ok: false, fieldErrors, formError };
}
